import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Wallet,
  Plus,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  ShoppingCart,
  Calendar,
  TrendingUp,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { motion } from "framer-motion";

const CashPage = () => {
  const [balance, setBalance] = useState({ total: 0, today: 0 });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [days, setDays] = useState(30);
  
  const [formData, setFormData] = useState({
    type: "deposit",
    amount: "",
    description: ""
  });

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    try {
      const [balanceRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/cash/balance`),
        axios.get(`${API}/cash/transactions?days=${days}`)
      ]);
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Kassendaten");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (type) => {
    setFormData({
      type: type,
      amount: "",
      description: ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      toast.error("Bitte gültigen Betrag eingeben");
      return;
    }

    try {
      await axios.post(`${API}/cash/transactions`, {
        type: formData.type,
        amount: formData.type === "withdrawal" ? -amount : amount,
        description: formData.description || null
      });

      toast.success(formData.type === "deposit" ? "Einzahlung gebucht" : "Entnahme gebucht");
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Fehler beim Buchen");
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case "sale":
        return <ShoppingCart className="w-4 h-4 text-emerald-600" />;
      case "deposit":
        return <ArrowDownRight className="w-4 h-4 text-blue-600" />;
      case "withdrawal":
        return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      default:
        return <RefreshCw className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTransactionColor = (type, amount) => {
    if (amount > 0) return "text-emerald-600";
    if (amount < 0) return "text-red-600";
    return "text-slate-600";
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case "sale": return "Verkauf";
      case "deposit": return "Einzahlung";
      case "withdrawal": return "Entnahme";
      case "correction": return "Korrektur";
      default: return type;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Group transactions by date
  const groupedTransactions = transactions.reduce((acc, t) => {
    const date = new Date(t.created_at).toLocaleDateString("de-DE");
    if (!acc[date]) acc[date] = [];
    acc[date].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Kasse
          </h1>
          <p className="text-slate-500 mt-1">
            Kassenstand und Buchungen verwalten
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => openDialog("deposit")}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Einzahlung
          </Button>
          <Button
            onClick={() => openDialog("withdrawal")}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <Minus className="w-4 h-4 mr-2" />
            Entnahme
          </Button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Kassenbestand</p>
                <p className="text-4xl font-bold text-emerald-700 mt-1">
                  {balance.total?.toFixed(2)}€
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Heute</p>
                <p className="text-4xl font-bold text-blue-700 mt-1">
                  {balance.today >= 0 ? "+" : ""}{balance.today?.toFixed(2)}€
                </p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Buchungen</p>
                <p className="text-4xl font-bold text-slate-700 mt-1">
                  {transactions.length}
                </p>
                <p className="text-xs text-slate-500">letzte {days} Tage</p>
              </div>
              <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-slate-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transactions */}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Buchungen
          </CardTitle>
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Tage</SelectItem>
              <SelectItem value="30">30 Tage</SelectItem>
              <SelectItem value="90">90 Tage</SelectItem>
              <SelectItem value="365">1 Jahr</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {Object.keys(groupedTransactions).length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Keine Buchungen in diesem Zeitraum
            </p>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedTransactions).map(([date, dayTransactions]) => {
                const dayTotal = dayTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
                
                return (
                  <div key={date}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-500">{date}</p>
                      <Badge variant={dayTotal >= 0 ? "default" : "destructive"}>
                        {dayTotal >= 0 ? "+" : ""}{dayTotal.toFixed(2)}€
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {dayTransactions.map((t) => (
                        <motion.div
                          key={t.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center border">
                              {getTransactionIcon(t.type)}
                            </div>
                            <div>
                              <p className="font-medium text-sm">
                                {getTransactionLabel(t.type)}
                              </p>
                              {t.description && (
                                <p className="text-xs text-slate-500">{t.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-mono font-bold ${getTransactionColor(t.type, t.amount)}`}>
                              {t.amount >= 0 ? "+" : ""}{t.amount?.toFixed(2)}€
                            </p>
                            <p className="text-xs text-slate-400">
                              {new Date(t.created_at).toLocaleTimeString("de-DE", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {formData.type === "deposit" ? (
                <>
                  <Plus className="w-5 h-5 text-emerald-600" />
                  Einzahlung
                </>
              ) : (
                <>
                  <Minus className="w-5 h-5 text-red-600" />
                  Entnahme
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className="text-xl font-mono"
              />
            </div>

            <div className="space-y-2">
              <Label>Beschreibung (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={formData.type === "deposit" 
                  ? "z.B. Wechselgeld aufgefüllt" 
                  : "z.B. Abrechnung Monat X"
                }
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDialogOpen(false)}
              >
                Abbrechen
              </Button>
              <Button
                className={`flex-1 ${
                  formData.type === "deposit"
                    ? "bg-emerald-600 hover:bg-emerald-700"
                    : "bg-red-600 hover:bg-red-700"
                }`}
                onClick={handleSubmit}
              >
                {formData.type === "deposit" ? "Einzahlen" : "Entnehmen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashPage;
